import axios from 'axios'

export interface Review {
  review_id: string
  user_id: string
  merchant_id: string
  reservation_id: string | null
  rating: number
  comment: string
  is_verified_purchase: boolean
  is_approved: boolean
  created_at: string
  updated_at: string
  user_first_name: string | null
  user_last_name: string | null
  merchant_name: string | null
}

export interface CreateReviewDto {
  user_id: string
  merchant_id: string
  reservation_id?: string
  rating: number
  comment?: string
}

export interface UpdateReviewDto {
  rating?: number
  comment?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  success: boolean
}

export interface ReviewFilters {
  user_id?: string
  merchant_id?: string
  rating?: number
  is_approved?: boolean
  is_verified_purchase?: boolean
}

export interface MerchantRatingSummary {
  merchant_id: string
  total_reviews: number
  average_rating: number
  rating_distribution: {
    1: number
    2: number
    3: number
    4: number
    5: number
  }
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const reviewApi = axios.create({
  baseURL: `${API_BASE_URL}/reviews`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
reviewApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
reviewApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const reviewService = {
  // Get all reviews with optional filters
  async getAll(filters: ReviewFilters = {}): Promise<PaginatedResponse<Review>> {
    const params = new URLSearchParams()
    
    if (filters.user_id) params.append('user_id', filters.user_id)
    if (filters.merchant_id) params.append('merchant_id', filters.merchant_id)
    if (filters.rating !== undefined) params.append('rating', filters.rating.toString())
    if (filters.is_approved !== undefined) params.append('is_approved', filters.is_approved.toString())
    if (filters.is_verified_purchase !== undefined) params.append('is_verified_purchase', filters.is_verified_purchase.toString())

    const response = await reviewApi.get('', { params })
    return response.data
  },

  // Get review by ID
  async getById(id: string): Promise<{ success: boolean; data: Review }> {
    const response = await reviewApi.get(`/${id}`)
    return response.data
  },

  // Get reviews for a specific merchant
  async getByMerchantId(merchantId: string): Promise<{ 
    success: boolean; 
    data: { 
      reviews: Review[]; 
      summary: {
        total_reviews: number;
        average_rating: number;
        rating_distribution: Record<string, number>;
      }
    } 
  }> {
    const response = await reviewApi.get(`/merchants/${merchantId}/reviews`)
    return response.data
  },

  // Get merchant rating summary
  async getMerchantRatingSummary(merchantId: string): Promise<{ 
    success: boolean; 
    data: MerchantRatingSummary 
  }> {
    const response = await reviewApi.get(`/merchants/${merchantId}/rating`)
    return response.data
  },

  // Create new review
  async create(reviewData: CreateReviewDto): Promise<{ success: boolean; data: Review; message: string }> {
    const response = await reviewApi.post('', reviewData)
    return response.data
  },

  // Update review
  async update(id: string, reviewData: UpdateReviewDto): Promise<{ success: boolean; data: Review; message: string }> {
    const response = await reviewApi.put(`/${id}`, reviewData)
    return response.data
  },

  // Delete review
  async delete(id: string): Promise<{ success: boolean; message: string }> {
    const response = await reviewApi.delete(`/${id}`)
    return response.data
  },

  // Update review approval status
  async updateApproval(id: string, is_approved: boolean): Promise<{ success: boolean; data: Review; message: string }> {
    const response = await reviewApi.patch(`/${id}/approval`, { is_approved })
    return response.data
  },

  // Get review statistics
  async getStatistics(): Promise<{
    total: number
    approved: number
    pending: number
    averageRating: number
    byRating: Record<number, number>
    verifiedPurchases: number
  }> {
    const allReviews = await this.getAll()
    const reviews = allReviews.data
    
    const byRating: Record<number, number> = {1: 0, 2: 0, 3: 0, 4: 0, 5: 0}
    
    reviews.forEach(review => {
      byRating[review.rating] = (byRating[review.rating] || 0) + 1
    })
    
    const stats = {
      total: reviews.length,
      approved: reviews.filter(r => r.is_approved).length,
      pending: reviews.filter(r => !r.is_approved).length,
      averageRating: reviews.length > 0 
        ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
        : 0,
      byRating,
      verifiedPurchases: reviews.filter(r => r.is_verified_purchase).length
    }
    
    return stats
  }
}

export default reviewService
